/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import {
	toMap, contrastingColor, getNameOrInitials, getPosterImageFromProfile,
} from '../../utils/utils';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { DRAG_TYPE, DropCallback } from '../../stores/reducers/user-interface';
import useShots from '../../hooks/use-shots';
import { useGetAV } from '../../contexts/audio-video';
import { appLogger } from '../../utils/datadog';
import { ROOM_STATES } from '../../stores/reducers/settings';

interface Props {
	video: VideoStream | undefined;
	feedPosition: number;
	grUser: number;
	dropCallback: DropCallback;
}

export default function FeedPresenter(props: Props) {
	const {
		video,
		feedPosition,
		grUser,
		dropCallback,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const videoElRef = useRef<HTMLVideoElement>(null);

	const { video: hasVideo } = useGetAV(video?.id);
	const { isOnCanvas } = useShots();

	const usersMapWebrtcId = useMemo(() => toMap('webrtcID', connectedUsers), [connectedUsers]);

	function getUser(id: string | number) {
		return usersMapWebrtcId.get(id as string);
	}

	function getPosterImage(id: string | number) {
		const mappedUser = usersMapWebrtcId.get(id as string);
		if (mappedUser && (mappedUser.poster || mappedUser.avatar)) {
			return mappedUser.poster || mappedUser.avatar;
		} if (user && id === 'local') {
			return user.bg_image || user.avatar;
		}
		return '';
	}

	function userFromId(id: string | number) {
		const connectedUser = connectedUsers.find((u) => u.gr_user === id);
		if (connectedUser) return connectedUser;
		const invite = invitations.find((i) => i.profile.gr_user === id);
		if (invite) {
			return invite.profile;
		}
		return {
			email: '',
			first_name: '',
			last_name: '',
			avatar: '',
			poster: '',
			job_title: '',
			gr_user: 0,
			picture: '',
			title: '',
		};
	}

	const colorStyles = useMemo(() => {
		const colorObj = {
			backgroundColor: '#333',
			color: contrastingColor('#333333'),
		};

		if (meeting?.primary_color) {
			colorObj.backgroundColor = `#${meeting.primary_color}`;
			colorObj.color = contrastingColor(meeting.primary_color);
		}

		return colorObj;
	}, [meeting]);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	async function playPresenterFeed(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;
		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					appLogger.error(
						'video el play request error',
						{ location: 'feed-presenter second' },
						e,
					);
				}
			}
		} catch (e: any) {
			appLogger.error(
				'video el play request error',
				{ location: 'feed-presenter first' },
				e,
			);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	const videoDebounce = useRef<NodeJS.Timeout | null>(null);

	// This attaches connectedPresenterVideo.srcObject to our video DOM element.
	// The srcObject is derived from OpenTok's publisher/subscriber object.
	// This has been known to cause issues where the user's video does not appear.
	// (Presenter appears as a black feed instead.)
	// green-4902
	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playPresenterFeed(video.srcObject);
			}, 100);
		}
	}, [video?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		<div ref={dropRef} className='feed-drop-area'>
			{video && video.srcObject && video?.srcObject.active
				? <>
					{isOver && <div className="dnd-highlighted" />}
					<video
						key={video.srcObject.id + feedPosition}
						className={classNames(!host && video.profile === user?.gr_user && 'mirrored')}
						muted={true}
						poster={blackImage}
						autoPlay={true}
						playsInline
						ref={videoElRef}
						onLoadedData={() => {
							// Force play on presenter video elements to avoid a
							// paused OpenTok publisher/subscriber feed.
							// green-4902
							videoElRef.current?.play().catch((e) => {
								appLogger.error('video el play request error', {
									location: 'feed-presenter third',
								}, e);
							});
						}}
					>
					</video>

					<div className={classNames('poster', { active: !hasVideo })}>
						{getPosterImage(video.id)
							? <img className="poster-image" src={getPosterImage(video.id)} alt={''} />
							: <div className="no-image-placeholder">
								<span style={colorStyles}>
									{getNameOrInitials(video.id !== 'local' ? getUser(video.id) : user, false)}
								</span>
							</div>
						}
					</div>

				</>
				: <>
					{isOver && <div className="dnd-highlighted" />}
					<div className={classNames('poster active')}>
						{(grUser && !!getPosterImageFromProfile(userFromId(grUser)))
							? <img className="poster-image" src={getPosterImageFromProfile(userFromId(grUser))} alt={''} />
							: <div className="no-image-placeholder">
								<span style={colorStyles}>{getNameOrInitials(userFromId(grUser), false)}</span>
							</div>
						}
					</div>
				</>
			}
		</div>
	);
}
